<template>
  <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
    <div class="px-4 py-3 border-b border-gray-200">
      <h2 class="heading-section">Call Details</h2>
    </div>
    <div class="p-4">
      <div v-if="!selectedCall" class="text-body text-gray-500">
        Select a call to view details
      </div>
      <div v-else class="space-y-6">
        <!-- Call Info Grid -->
        <div class="grid grid-cols-2 gap-x-6 gap-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
            <p class="sensitive text-body">{{ selectedCall.to_phone_number }}</p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <span :class="['px-3 py-1.5 text-xs font-semibold rounded-full border', getStatusColorClass(formatLabel(String(selectedCall.status || '')))]">
              {{ formatLabel(String(selectedCall.status || '')) }}
            </span>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Started</label>
            <p class="text-body">
              {{ selectedCall.started_at ? formatRelativeTime(String(selectedCall.started_at)) : '-' }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Duration</label>
            <p class="text-body">
              {{ formatDuration(String(selectedCall.started_at), String(selectedCall.ended_at)) }}
            </p>
          </div>
          <div class="col-span-2">
            <label class="block text-sm font-medium text-gray-700 mb-1">Configuration</label>
            <div class="flex items-center gap-2">
              <p class="text-body">{{ selectedCall.configuration_name || '-' }}</p>
              <button
                v-if="selectedCall.configuration"
                class="btn-primary-sm"
                @click="$emit('open-config-modal', selectedCall.configuration)"
              >
                <font-awesome-icon icon="pen-to-square" />
              </button>
            </div>
          </div>
        </div>

        <!-- Call Data Section -->
        <div v-if="hasMetadata" class="border-t-2 border-gray-300 pt-6 mt-6">
          <h3 class="text-base font-semibold text-gray-900 mb-3">Call Data</h3>
          <div class="sensitive grid grid-cols-2 gap-x-6 gap-y-3">
            <div v-for="(value, key) in selectedCall.metadata" :key="key">
              <label class="block text-xs font-medium text-gray-500 mb-1">{{ key }}</label>
              <p class="text-body">{{ value }}</p>
            </div>
          </div>
        </div>

        <!-- Appointment Section -->
        <div v-if="selectedCall.appointment" class="border-t-2 border-gray-300 pt-6 mt-6">
          <h3 class="text-base font-semibold text-gray-900 mb-3">Appointment Booked</h3>
          <div class="grid grid-cols-2 gap-x-6 gap-y-3">
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Confirmation Number</label>
              <p class="text-body font-semibold text-green-700">{{ selectedCall.appointment.confirmation_number }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Appointment Type</label>
              <p class="text-body">{{ selectedCall.appointment.appointment_type }}</p>
            </div>
            <div>
              <label class="block text-xs font-medium text-gray-500 mb-1">Date & Time</label>
              <p class="text-body">{{ formatAppointmentDateTime(selectedCall.appointment.appointment_datetime) }}</p>
            </div>
            <div v-if="selectedCall.appointment.external_slot_id">
              <label class="block text-xs font-medium text-gray-500 mb-1">External Slot ID</label>
              <p class="text-body">{{ selectedCall.appointment.external_slot_id }}</p>
            </div>
          </div>
        </div>

        <!-- Summary Section -->
        <div class="border-t-2 border-gray-300 pt-6 mt-6">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-base font-semibold text-gray-900">Call Summary</h3>
            <button
              :disabled="generatingSummary || !selectedCall.transcript"
              class="font-semibold rounded-md transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed h-8 px-3 py-2 text-xs"
              :class="selectedCall.summary && Object.keys(selectedCall.summary).length > 0 
                ? 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300' 
                : 'bg-primary-500 hover:bg-primary-600 text-white'"
              @click="generateSummary"
            >
              <font-awesome-icon v-if="generatingSummary" icon="spinner" spin />
              <font-awesome-icon v-else icon="wand-magic-sparkles" />
              {{ generatingSummary 
                ? 'Generating...' 
                : (selectedCall.summary && Object.keys(selectedCall.summary).length > 0 
                  ? 'Regenerate Summary' 
                  : 'Generate Summary') 
              }}
            </button>
          </div>
          
          <div v-if="summaryError" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p class="text-sm text-red-800">{{ summaryError }}</p>
          </div>
          
          <div v-if="selectedCall.summary && Object.keys(selectedCall.summary).length > 0" class="space-y-4">
            <!-- Highlighted Fields: Outcome & Sentiment -->
            <div v-if="selectedCall.summary.call_outcome || selectedCall.summary.patient_sentiment" class="flex gap-4">
              <div v-if="selectedCall.summary.call_outcome">
                <label class="block text-xs font-medium text-gray-500 mb-1">Outcome</label>
                <span :class="['px-3 py-1.5 text-xs font-semibold rounded-full border inline-block', getOutcomeColorClass(selectedCall.summary.call_outcome)]">
                  {{ formatLabel(selectedCall.summary.call_outcome) }}
                </span>
              </div>
              
              <div v-if="selectedCall.summary.patient_sentiment">
                <label class="block text-xs font-medium text-gray-500 mb-1">Sentiment</label>
                <span :class="['px-3 py-1.5 text-xs font-semibold rounded-full border inline-block', getSentimentColorClass(selectedCall.summary.patient_sentiment)]">
                  {{ formatLabel(selectedCall.summary.patient_sentiment) }}
                </span>
              </div>
            </div>
            
            <!-- All Other Fields (Generic) -->
            <div 
              v-for="(value, key) in selectedCall.summary" 
              v-show="!['call_outcome', 'patient_sentiment'].includes(key) && value && (Array.isArray(value) ? value.length > 0 : true)"
              :key="key"
            >
              <label class="block text-xs font-medium text-gray-500 mb-1">{{ formatLabel(key) }}</label>
              
              <!-- Array values -->
              <ul v-if="Array.isArray(value)" class="list-disc list-inside space-y-1">
                <li v-for="(item, index) in value" :key="index" class="text-body">{{ item }}</li>
              </ul>
              
              <!-- String/other values -->
              <p v-else class="text-body">{{ value }}</p>
            </div>
          </div>
          <div v-else-if="!selectedCall.transcript" class="text-body text-gray-500 italic">
            No transcript available to generate summary
          </div>
          <div v-else class="text-body text-gray-500 italic">
            No summary generated yet. Click "Generate Summary" to analyze this call.
          </div>
        </div>

        <!-- Transcript Section -->
        <div class="border-t-2 border-gray-300 pt-6 mt-6">
          <h3 class="text-base font-semibold text-gray-900 mb-3">Conversation</h3>
          <div v-if="conversationMessages.length > 0" class="space-y-3 max-h-[72rem] overflow-y-auto pr-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div 
              v-for="(message, index) in conversationMessages" 
              :key="index"
              :class="[
                'rounded-lg p-4',
                message.role === 'user' 
                  ? 'bg-orange-50 border border-orange-200 ml-8' 
                  : 'bg-purple-50 border border-purple-200 mr-8'
              ]"
            >
              <div class="flex items-center gap-2 mb-2">
                <span 
                  :class="[
                    'text-xs font-semibold px-2 py-1 rounded',
                    message.role === 'user' 
                      ? 'bg-primary-500 text-white' 
                      : 'bg-purple-600 text-white'
                  ]"
                >
                  {{ message.role === 'user' ? 'Person' : 'AI' }}
                </span>
              </div>
              <p class="sensitive text-body leading-relaxed" data-demo-blur>{{ message.text }}</p>
            </div>
          </div>
          <div v-else class="mt-2 text-body text-gray-500 italic">
            No conversation available
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { formatRelativeTime, formatDuration } from '@/utils/date'
import { formatLabel } from '@/utils/string'

const BADGE_COLORS = {
  success: 'bg-green-100 text-green-800 border-green-300',
  warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  danger: 'bg-red-100 text-red-800 border-red-300',
  info: 'bg-blue-100 text-blue-800 border-blue-300',
  neutral: 'bg-gray-100 text-gray-800 border-gray-300',
}

export default {
  name: 'CallDetailsCard',
  props: {
    selectedCall: {
      type: Object,
      default: null,
    },
  },
  emits: ['open-config-modal', 'summary-generated'],
  data() {
    return {
      generatingSummary: false,
      summaryError: null,
    }
  },
  computed: {
    hasMetadata() {
      return this.selectedCall?.metadata && Object.keys(this.selectedCall.metadata).length > 0
    },
    conversationMessages() {
      if (!this.selectedCall?.conversation_history || !Array.isArray(this.selectedCall.conversation_history)) {
        return []
      }

      return this.selectedCall.conversation_history
        .filter(item => item.role !== 'system')
        .map(item => {
          const content = item.content || []
          let text = ''

          // Extract text based on content type
          for (const contentItem of content) {
            const type = contentItem.type || ''
            
            if (type === 'input_audio') {
              text = contentItem.transcript || ''
            } else if (type === 'output_audio') {
              text = contentItem.transcript || ''
            } else if (type === 'output_text') {
              text = contentItem.text || ''
            }
            
            if (text) break
          }

          return {
            role: item.role,
            text: text || '(No text available)'
          }
        })
        .filter(msg => msg.text && msg.text !== '(No text available)')
    }
  },
  methods: {
    formatRelativeTime,
    formatDuration,
    formatLabel,
    async generateSummary() {
      if (!this.selectedCall?.id) return
      
      const regenerate = this.selectedCall.summary && Object.keys(this.selectedCall.summary).length > 0
      
      // Confirm if regenerating
      if (regenerate) {
        const confirmed = confirm('This will regenerate the call summary. Continue?')
        if (!confirmed) return
      }
      
      this.generatingSummary = true
      this.summaryError = null
      
      try {
        const response = await axios.post(
          `api/outreach/calls/${this.selectedCall.id}/generate_summary/`,
          { regenerate }
        )
        
        const data = response.data
        
        if (!data.success) {
          throw new Error(data.error || 'Failed to generate summary')
        }
        
        this.$emit('summary-generated', data.call)
        
      } catch (error) {
        console.error('Error generating summary:', error)
        this.summaryError = error.response?.data?.error || error.message || 'Failed to generate summary. Please try again.'
      } finally {
        this.generatingSummary = false
      }
    },
    formatAppointmentDateTime(datetimeStr) {
      if (!datetimeStr) return '-'
      const date = new Date(datetimeStr)
      return date.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZoneName: 'short'
      })
    },
    getStatusColorClass(status) {
      const statusLower = (status || '').toLowerCase()
      const callColors = {
        completed: BADGE_COLORS.success,
        in_progress: BADGE_COLORS.info,
        'in progress': BADGE_COLORS.info,
        ringing: BADGE_COLORS.info,
        pending: BADGE_COLORS.warning,
        failed: BADGE_COLORS.danger,
        no_answer: BADGE_COLORS.neutral,
        'no answer': BADGE_COLORS.neutral,
        busy: BADGE_COLORS.neutral,
      }
      return callColors[statusLower] || BADGE_COLORS.neutral
    },
    getOutcomeColorClass(outcome) {
      const outcomeLower = (outcome || '').toLowerCase()
      const outcomeColors = {
        appointment_scheduled: BADGE_COLORS.success,
        'appointment scheduled': BADGE_COLORS.success,
        callback_requested: BADGE_COLORS.info,
        'callback requested': BADGE_COLORS.info,
        declined: BADGE_COLORS.danger,
        no_answer: BADGE_COLORS.neutral,
        'no answer': BADGE_COLORS.neutral,
        left_voicemail: BADGE_COLORS.info,
        'left voicemail': BADGE_COLORS.info,
        wrong_number: BADGE_COLORS.warning,
        'wrong number': BADGE_COLORS.warning,
        other: BADGE_COLORS.neutral,
      }
      return outcomeColors[outcomeLower] || BADGE_COLORS.neutral
    },
    getSentimentColorClass(sentiment) {
      const sentimentLower = (sentiment || '').toLowerCase()
      const sentimentColors = {
        positive: BADGE_COLORS.success,
        neutral: BADGE_COLORS.info,
        negative: BADGE_COLORS.danger,
        concerned: BADGE_COLORS.warning,
        unable_to_assess: BADGE_COLORS.neutral,
        'unable to assess': BADGE_COLORS.neutral,
      }
      return sentimentColors[sentimentLower] || BADGE_COLORS.neutral
    },
  },
}
</script>
